import React, { useEffect } from "react";
import {
    ChevronDown,
    BookOpen,
    FileCode2,
    Edit3,
    FileOutput,
    Palette,
    Keyboard,
    CheckCircle,
    Layers,
    FileText,
    Monitor,
    Settings,
    ArrowRight,
    ArrowDown,
    Check,
    Zap,
    ShieldCheck,
    TrendingUp,
    Clock
} from "lucide-react";

const slidesData = [
    {
        isOverview: true,
        title: "Vedas Publishing Workflow",
        badge: "Overview"
    },
    {
        title: "Word to XML Conversion",
        subtitle: "Automated Structuring",
        description: "Convert unstructured author submissions into structured, standards-compliant XML format. This reduces formatting overhead and ensures data consistency throughout the publishing lifecycle.",
        icon: FileCode2,
        badge: "Architecture",
        features: [
            "Maintains complex document hierarchy and semantics",
            "Extracts critical metadata automatically",
            "Handles complex tables and inline equations gracefully"
        ],
        metrics: [
            { label: "Formatting Time Saved", value: "85%", icon: Clock },
            { label: "Structural Accuracy", value: "99.9%", icon: ShieldCheck }
        ]
    },
    {
        title: "Native XML WYSIWYG Editor",
        subtitle: "Zero Translation Loss",
        description: "Operate directly on XML semantic structures without relying on HTML translations. What you edit on screen accurately and safely reflects the underlying XML schema.",
        icon: Edit3,
        badge: "Technology",
        features: [
            "Real-time underlying schema validation",
            "Intuitive interface designed for non-technical users",
            "Extensible support for custom publisher XML tags"
        ],
        metrics: [
            { label: "Data Translation Loss", value: "0%", icon: Zap },
            { label: "Editor Responsiveness", value: "< 50ms", icon: TrendingUp }
        ]
    },
    {
        title: "Direct PDF Compilation",
        subtitle: "XML to LaTeX Pipeline",
        description: "Compile and view high-fidelity PDF documents instantly. Our streamlined system passes XML directly through LaTeX, allowing for rapid visual feedback and typesetting.",
        icon: FileOutput,
        badge: "Performance",
        features: [
            "High-quality, publisher-grade document typesetting",
            "Eliminates intermediary file format conversions",
            "Immediate visual layout feedback for editing authors"
        ],
        metrics: [
            { label: "Compilation Speed", value: "Superfast", icon: Zap },
            { label: "Output Fidelity", value: "100%", icon: ShieldCheck }
        ]
    },
    {
        title: "Publisher Specifications",
        subtitle: "Built-in Style Functions",
        description: "Ensure that document outputs align perfectly with specific publisher layouts and formatting guidelines, producing print-ready materials right out of the editor.",
        icon: Palette,
        badge: "Compliance",
        features: [
            "Pre-configured, robust journal formatting templates",
            "Automated layout property adjustments",
            "Strict adherence to industry-standard layout rules"
        ],
        metrics: [
            { label: "Template Compliance", value: "Strict", icon: ShieldCheck },
            { label: "Post-edit Revisions", value: "-70%", icon: TrendingUp }
        ]
    },
    {
        title: "Author Editing Experience",
        subtitle: "Familiar and Intuitive",
        description: "Provide authors and editors with a comfortable, Word-like interface. This familiar environment neatly manages the complex XML architecture running in the background.",
        icon: Keyboard,
        badge: "User Experience",
        features: [
            "Familiar word-processor controls and shortcuts",
            "Integrated track changes and collaboration features",
            "Focus mode for distraction-free manuscript drafting"
        ],
        metrics: [
            { label: "Learning Curve", value: "Minimal", icon: Clock },
            { label: "Author Satisfaction", value: "High", icon: ShieldCheck }
        ]
    },
    {
        title: "Validation & Structuring",
        subtitle: "Smart Content Enrichment",
        description: "Automate time-consuming tasks with integrated reference structuring, metadata enrichment, and DOI lookups, significantly reducing manual review time.",
        icon: CheckCircle,
        badge: "Automation",
        features: [
            "Crossref API integration for seamless DOI lookups",
            "Automated reference and citation structure correction",
            "Intelligent author and affiliation metadata matching"
        ],
        metrics: [
            { label: "Manual Review Time", value: "-60%", icon: Clock },
            { label: "Metadata Accuracy", value: "Enriched", icon: Zap }
        ]
    },
    {
        title: "Complete Publishing Ecosystem",
        subtitle: "End-to-End Capabilities",
        description: "An integrated environment featuring interactive structural tree views, robust mathematical equation editing, and centralized asset management to support the entire publishing process.",
        icon: Layers,
        badge: "Platform",
        features: [
            "Centralized media and asset repository management",
            "MathLive integration for native LaTeX equation input",
            "Granular version control with complete document history"
        ],
        metrics: [
            { label: "Workflow Integration", value: "Seamless", icon: ShieldCheck },
            { label: "Platform Uptime", value: "Reliable", icon: TrendingUp }
        ]
    }
];

const OverviewFlowchart = () => {
    const steps = [
        { title: "Input Word", desc: "Author manuscript", icon: FileText },
        { title: "XML", desc: "Automated structure", icon: FileCode2 },
        { title: "VEDAS Editor", desc: "WYSIWYG editing", icon: Monitor },
        { title: "LaTeX", desc: "Typesetting engine", icon: Settings },
        { title: "PDF", desc: "Print-ready output", icon: FileOutput }
    ];

    return (
        <div className="w-full mt-10">
            <div className="flex flex-col xl:flex-row items-center justify-between gap-4 w-full">
                {steps.map((step, idx) => (
                    <React.Fragment key={idx}>
                        {/* Step Card */}
                        <div className="flex flex-col items-center bg-white p-6 rounded-xl shadow-md border border-gray-200 flex-1 w-full xl:w-auto text-center hover:-translate-y-1 transition-transform duration-300">
                            <div className="w-16 h-16 bg-blue-50 rounded-full flex justify-center items-center text-blue-600 mb-4 shadow-inner">
                                <step.icon size={32} strokeWidth={1.5} />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">{step.title}</h3>
                            <p className="text-sm text-gray-500">{step.desc}</p>
                        </div>

                        {/* Connectors */}
                        {idx < steps.length - 1 && (
                            <div className="flex justify-center items-center text-blue-300">
                                <ArrowRight size={32} className="hidden xl:block" />
                                <ArrowDown size={32} className="block xl:hidden my-2" />
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

const Presentation = () => {
    useEffect(() => {
        document.body.style.overflow = "hidden";
        document.body.style.paddingTop = "0px";
        return () => {
            document.body.style.overflow = "auto";
            document.body.style.paddingTop = "122px";
        };
    }, []);

    return (
        <div className="h-screen w-full overflow-y-scroll snap-y snap-mandatory bg-slate-50 scroll-smooth relative font-sans text-slate-800" style={{ scrollbarWidth: "none" }}>
            <style dangerouslySetInnerHTML={{
                __html: `
        ::-webkit-scrollbar {
          width: 0px;
          background: transparent;
        }
      `}} />

            {/* Global Background Grid Pattern for corporate feel */}
            <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-0"
                style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '24px 24px' }}>
            </div>

            {slidesData.map((slide, index) => {
                if (slide.isOverview) {
                    return (
                        <section
                            key={index}
                            className="h-screen w-full snap-start flex flex-col justify-center items-center relative px-6 lg:px-12 py-12 z-10"
                        >
                            <div className="w-full max-w-7xl mx-auto flex flex-col items-center justify-center">
                                <div className="bg-white p-12 md:p-16 rounded-[2rem] shadow-xl border border-slate-100 w-full relative overflow-hidden">
                                    {/* Decorative background shape */}
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-bl-full opacity-50 -z-10"></div>

                                    <div className="text-center mb-12">
                                        <span className="px-4 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-widest mb-6 inline-block border border-blue-100">
                                            {slide.badge}
                                        </span>
                                        <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 tracking-tight mb-4">
                                            {slide.title}
                                        </h1>
                                        <p className="text-lg md:text-xl text-slate-500 max-w-3xl mx-auto">
                                            A high-level map of the end-to-end manuscript transformation pipeline. Streamlining complex publishing tasks into one cohesive ecosystem.
                                        </p>
                                    </div>

                                    <OverviewFlowchart />

                                    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center border-t border-slate-100 pt-10">
                                        <div>
                                            <h4 className="text-3xl font-bold text-blue-600 mb-2">10x</h4>
                                            <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Faster Turnaround</p>
                                        </div>
                                        <div>
                                            <h4 className="text-3xl font-bold text-blue-600 mb-2">100%</h4>
                                            <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">XML Fidelity</p>
                                        </div>
                                        <div>
                                            <h4 className="text-3xl font-bold text-blue-600 mb-2">Zero</h4>
                                            <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Translation Errors</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom indicator */}
                            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center opacity-50 text-slate-400">
                                <span className="text-sm font-medium mb-1 tracking-widest">
                                    {index + 1} / {slidesData.length}
                                </span>
                                {index !== slidesData.length - 1 && <ChevronDown size={20} className="animate-bounce mt-1" />}
                            </div>
                        </section>
                    );
                }

                const IconComponent = slide.icon;

                return (
                    <section
                        key={index}
                        className="h-screen w-full snap-start flex flex-col justify-center items-center relative px-6 lg:px-12 py-12 z-10"
                    >
                        <div className="w-full max-w-7xl mx-auto bg-white rounded-[2rem] shadow-xl border border-slate-100 overflow-hidden flex flex-col lg:flex-row h-auto lg:h-[70vh]">

                            {/* Left Column (Visual & Title) */}
                            <div className="w-full lg:w-5/12 bg-slate-50 p-12 lg:p-16 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-slate-100 relative overflow-hidden">
                                <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-blue-50/50 to-transparent"></div>

                                <div className="relative z-10">
                                    <div className="w-20 h-20 bg-white rounded-2xl flex justify-center items-center text-blue-600 mb-8 shadow-sm border border-slate-100">
                                        <IconComponent size={36} strokeWidth={2} />
                                    </div>
                                    <span className="px-3 py-1 rounded-full bg-white text-slate-600 text-xs font-bold uppercase tracking-wider mb-4 border border-slate-200 inline-block">
                                        {slide.badge}
                                    </span>
                                    <h1 className="text-3xl lg:text-5xl font-extrabold mb-4 text-slate-900 tracking-tight leading-tight">
                                        {slide.title}
                                    </h1>
                                </div>
                            </div>

                            {/* Right Column (Content & Data) */}
                            <div className="w-full lg:w-7/12 p-12 lg:p-16 flex flex-col justify-center bg-white relative">
                                <h2 className="text-xl lg:text-2xl font-semibold text-blue-600 mb-4 flex items-center">
                                    {slide.subtitle}
                                </h2>
                                <p className="text-lg text-slate-600 leading-relaxed mb-10">
                                    {slide.description}
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                                    {/* Key Features List */}
                                    <div>
                                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest mb-4 border-b border-slate-100 pb-2">Key Capabilities</h3>
                                        <ul className="space-y-4">
                                            {slide.features.map((feature, fIdx) => (
                                                <li key={fIdx} className="flex items-start text-slate-600 text-sm">
                                                    <Check size={18} className="text-emerald-500 mr-3 flex-shrink-0 mt-0.5" />
                                                    <span className="leading-snug">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Metrics Block */}
                                    <div>
                                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest mb-4 border-b border-slate-100 pb-2">Business Impact</h3>
                                        <div className="space-y-4">
                                            {slide.metrics.map((metric, mIdx) => (
                                                <div key={mIdx} className="bg-slate-50 rounded-lg p-4 border border-slate-100 flex items-center">
                                                    <div className="w-10 h-10 rounded-full bg-white flex justify-center items-center shadow-sm text-blue-500 mr-4 flex-shrink-0">
                                                        <metric.icon size={20} />
                                                    </div>
                                                    <div>
                                                        <div className="text-xl font-bold text-slate-900 leading-none mb-1">{metric.value}</div>
                                                        <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{metric.label}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* Bottom indicator */}
                        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center opacity-50 text-slate-400">
                            <span className="text-sm font-medium mb-1 tracking-widest">
                                {index + 1} / {slidesData.length}
                            </span>
                            {index !== slidesData.length - 1 && <ChevronDown size={20} className="animate-bounce mt-1" />}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default Presentation;
